/* Motion for the PoD page: numbers count up, the speedometer needle swings in and settles, bars grow from zero.
   Everything runs from the previous value to the new one (so changing a filter animates the change), and is skipped
   for users who ask for reduced motion. */
import { useEffect, useRef, useState } from 'react'
import { keyframes } from '@mui/material'

export const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export const easeOutCubic = (t) => 1 - (1 - t) ** 3
/** Swings a little past the end and settles back, like a real needle. */
export const easeOutBack = (t) => { const c = 1.3; return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2 }

/**
 * Animates from the last value (or `from` the first time) to `to`. Returns { t, value } where t is 0→1 progress
 * (eased with `ease`) and value the eased number. `to` null → value null.
 */
export function useTween(to, { from = 0, ms = 1100, ease = easeOutCubic } = {}) {
  const [state, setState] = useState({ t: 1, value: to })
  const last = useRef(null)
  useEffect(() => {
    if (to == null) { last.current = null; setState({ t: 1, value: null }); return undefined }
    const start = last.current ?? from
    last.current = to
    if (reducedMotion() || start === to) { setState({ t: 1, value: to }); return undefined }
    let raf
    const t0 = performance.now()
    const step = (now) => {
      const p = Math.min(1, (now - t0) / ms)
      const e = ease(p)
      setState({ t: p, value: start + (to - start) * e })
      if (p < 1) raf = requestAnimationFrame(step)
    }
    setState({ t: 0, value: start })
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [to]) // eslint-disable-line react-hooks/exhaustive-deps
  return state
}

/** A number that counts up to `value`, formatted with `format` (Indian grouping by default). */
export function CountUp({ value, decimals = 0, format, ms = 900 }) {
  const { value: v } = useTween(value == null ? null : Number(value), { ms })
  if (v == null) return '—'
  const n = decimals ? Math.round(v * 10 ** decimals) / 10 ** decimals : Math.round(v)
  return format ? format(n) : n.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

/** Bar fill growing from the left; pair with transformOrigin 'left center'. */
export const growX = keyframes`from { transform: scaleX(0); } to { transform: scaleX(1); }`
export const fadeUp = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`
/** sx for a one-off entrance animation that is switched off under reduced motion. */
export const enter = (anim, ms = 700, delay = 0) => ({
  animation: `${anim} ${ms}ms cubic-bezier(.2,.8,.2,1) ${delay}ms both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
})
